import React, { useState } from "react";
import SbuxStyledTableRow from "../../../../components/SbuxTable/SbuxStyledTableRow";
import SbuxStyledTableCell from "../../../../components/SbuxTable/SbuxStyledTableCell";
import SbuxStackSnackbar from "../../../../components/SbuxStackSnackbar";
import { IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { useAppDispatch, useAppSelector } from "../../../../store/types";
import {
  getMediaExpandCollapseInfo,
  getMediaFile,
  MediaFileArg,
} from "../../../../services/mediaMgt";
import {
  PLEASE_WAIT,
  SNACK_BARS_SEVERITY_SUCCESS,
  SNACK_BARS_SEVERITY_ERROR,
  OOPS_ERROR_MESSAGE,
} from "../../../../constants";
import {
  getIsPendingSelector,
  getIsErrorSelector,
  getMediaExpandCollapseInfoSelector,
} from "../../../../selectors/mediaMgtSelector";
import type { MediaExpandCollapseInfo } from "./types";

interface Props {
  mediaId: string;
  mediaObj: MediaFileArg;
  children: React.ReactNode;
  expandComponent: React.ReactNode;
  isHighlighted?: boolean;
}

const ExpandableRow = ({
  mediaId,
  mediaObj,
  children,
  expandComponent,
  isHighlighted,
}: Props) => {
  const dispatch = useAppDispatch();

  const isPending = useAppSelector(getIsPendingSelector);
  const isError = useAppSelector(getIsErrorSelector);
  const mediaExpandCollapseInfo = useAppSelector(
    getMediaExpandCollapseInfoSelector,
  ) as MediaExpandCollapseInfo | null;

  const [openSnackBar] = useState(false);
  const isExpanded = false;
  const handleExpand = async () => {
    if (
      mediaExpandCollapseInfo &&
      mediaId === mediaExpandCollapseInfo.mediaId
    ) {
      if (!mediaExpandCollapseInfo.isExpanded) {
        await dispatch(getMediaFile(mediaObj));
      }
      await dispatch(
        getMediaExpandCollapseInfo({
          mediaId: mediaId,
          isExpanded: !mediaExpandCollapseInfo.isExpanded,
        }),
      );
    } else {
      if (!isExpanded) {
        await dispatch(getMediaFile(mediaObj));
      }
      await dispatch(
        getMediaExpandCollapseInfo({
          mediaId: mediaId,
          isExpanded: !isExpanded,
        }),
      );
    }
  };

  const handleClose = () => {
    // handled by SbuxStackSnackbar internally
  };

  return (
    <>
      {openSnackBar && (isPending || isError) && (
        <SbuxStackSnackbar
          open={openSnackBar}
          message={isPending ? PLEASE_WAIT : isError ? OOPS_ERROR_MESSAGE : ""}
          severity={
            isError ? SNACK_BARS_SEVERITY_ERROR : SNACK_BARS_SEVERITY_SUCCESS
          }
          handleClose={handleClose}
        />
      )}
      <SbuxStyledTableRow isHighlighted={isHighlighted}>
        <SbuxStyledTableCell padding="checkbox">
          <IconButton
            disableFocusRipple
            disableRipple
            onClick={handleExpand}
            sx={{ outline: "0 !important" }}
          >
            {mediaExpandCollapseInfo &&
            mediaExpandCollapseInfo.mediaId === mediaId &&
            mediaExpandCollapseInfo.isExpanded ? (
              <KeyboardArrowDown sx={{ fontSize: 24, marginLeft: -1.5 }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 24, marginLeft: -1.5 }} />
            )}
          </IconButton>
        </SbuxStyledTableCell>
        {children}
      </SbuxStyledTableRow>
      {mediaExpandCollapseInfo &&
        mediaExpandCollapseInfo.mediaId === mediaId &&
        mediaExpandCollapseInfo.isExpanded && (
          <SbuxStyledTableRow sx={{ "& > *": { borderBottom: "unset" } }}>
            <SbuxStyledTableCell padding="checkbox" />
            {expandComponent}
          </SbuxStyledTableRow>
        )}
    </>
  );
};

export default ExpandableRow;
